import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditIcon from "@mui/icons-material/Edit";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";

import BrandPagination from "../../layouts/Pagination";
import SearchBar from "../../shared/components/SearchBar";
import BrandSpinner from "../../shared/components/BrandSpinner";
import BackButton from "../../shared/components/BackButton";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import api from "../../services/api/axios";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  PRIMARY_BUTTON_SX,
  SECONDARY_BUTTON_SX,
} from "../../theme/buttonstyles";
import { FILTER_STYLES } from "../../theme/filterstyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import MembershipEventDialog from "./MembershipEventDialog";
import useMembershipEventsData from "./hooks/useMembershipEventsData";
import { MEMBERSHIP_TOOLTIP_PROPS } from "./tooltipStyles";

const PAGE_BG = "#f7f9fc";
const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};
const HEAD_CELL_SX = {
  backgroundColor: BRAND_BLUE,
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
};
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    color: BRAND_BLUE,
    background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
    boxShadow: "0 4px 14px rgba(0,51,108,0.10)",
    "& fieldset": { border: `2px solid ${BRAND_BLUE}` },
    "&:hover fieldset": { borderColor: BRAND_BLUE },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
    color: BRAND_BLUE,
    fontWeight: 600,
  },
};

// MUI v7's Grid dropped the item/xs/sm/md breakpoint props in favor of `size`;
// plain CSS grids avoid relying on that API and keep spans explicit.
const AUTO_GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "16px",
};

const FORM_ROW_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(12, 1fr)" },
  gap: "16px",
};

function Field({ span, children }) {
  return <Box sx={{ gridColumn: { xs: "span 1", sm: `span ${span}` } }}>{children}</Box>;
}

function display(value) {
  return value == null || value === "" ? "-" : String(value);
}
function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
function formatMoney(value) {
  if (value == null || value === "") return "-";
  return Number(value).toLocaleString(undefined, { style: "currency", currency: "USD" });
}
function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
async function listEndpoint(endpoint, params) {
  const response = await api.get(endpoint, { params: { limit: 500, offset: 0, ...params } });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

function MetricCard({ label, value, helper }) {
  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography sx={{ color: "#52677f", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
        <Typography sx={{ color: BRAND_BLUE, fontSize: 24, fontWeight: 800, mt: 1 }}>{value}</Typography>
        {helper ? <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.75 }}>{helper}</Typography> : null}
      </CardContent>
    </Card>
  );
}

const EMPTY_ATTENDANCE = {
  event_id: "",
  member_id: "",
  contact_id: "",
  status: "registered",
  check_in_status: "not_checked_in",
  fee_paid: "",
  fee_paid_amount: "",
  attended_at: "",
  guests_count: 0,
  is_sponsor_attendee: false,
  is_speaker: false,
  is_exhibitor: false,
  notes: "",
};

function AttendanceDialog({ open, attendance, eventId, members, contacts, saving, onClose, onSubmit }) {
  const [values, setValues] = useState(EMPTY_ATTENDANCE);

  useEffect(() => {
    if (!open) return;
    setValues({
      ...EMPTY_ATTENDANCE,
      ...(attendance || {}),
      event_id: attendance?.event_id || eventId,
      member_id: attendance?.member_id || "",
      contact_id: attendance?.contact_id || "",
      fee_paid: attendance?.fee_paid == null ? "" : Number(attendance.fee_paid) > 0 ? "yes" : "no",
      fee_paid_amount: attendance?.fee_paid != null && Number(attendance.fee_paid) > 0 ? attendance.fee_paid : "",
      guests_count: attendance?.guests_count ?? 0,
      attended_at: toDateTimeLocal(attendance?.attended_at),
      is_sponsor_attendee: Boolean(attendance?.is_sponsor_attendee),
      is_speaker: Boolean(attendance?.is_speaker),
      is_exhibitor: Boolean(attendance?.is_exhibitor),
    });
  }, [attendance, eventId, open]);

  const setValue = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const submit = () => onSubmit({
    event_id: Number(values.event_id),
    member_id: values.member_id ? Number(values.member_id) : null,
    contact_id: values.contact_id ? Number(values.contact_id) : null,
    status: values.status,
    check_in_status: values.check_in_status || null,
    fee_paid:
      values.fee_paid === ""
        ? null
        : values.fee_paid === "yes"
          ? Number(values.fee_paid_amount || 0)
          : 0,
    attended_at: values.attended_at ? new Date(values.attended_at).toISOString() : null,
    guests_count: Number(values.guests_count || 0),
    is_sponsor_attendee: values.is_sponsor_attendee,
    is_speaker: values.is_speaker,
    is_exhibitor: values.is_exhibitor,
    notes: values.notes || null,
  });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{attendance?.id ? "Edit Attendance" : "Add Attendance"}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fbfcf8" }}>
        <Box sx={{ ...FORM_ROW_SX, pt: 1 }}>
          <Field span={6}>
            <TextField select label="Member Attendee" value={values.member_id} onChange={setValue("member_id")} fullWidth sx={FIELD_SX}>
              <MenuItem value="">No member</MenuItem>
              {members.map((member) => <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>)}
            </TextField>
          </Field>
          <Field span={6}>
            <TextField select label="Contact Attendee" value={values.contact_id} onChange={setValue("contact_id")} fullWidth sx={FIELD_SX}>
              <MenuItem value="">No contact</MenuItem>
              {contacts.map((contact) => <MenuItem key={contact.id} value={contact.id}>{contact.full_name}</MenuItem>)}
            </TextField>
          </Field>
          <Field span={4}>
            <TextField select label="Registration Status" value={values.status} onChange={setValue("status")} fullWidth sx={FIELD_SX}>
              {["registered", "attended", "cancelled", "no_show"].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
          </Field>
          <Field span={4}>
            <TextField select label="Check-in Status" value={values.check_in_status} onChange={setValue("check_in_status")} fullWidth sx={FIELD_SX}>
              {["not_checked_in", "checked_in", "no_show"].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
          </Field>
          <Field span={4}>
            <TextField select label="Fee Paid" value={values.fee_paid} onChange={setValue("fee_paid")} fullWidth sx={FIELD_SX}>
              <MenuItem value="">Unknown</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Field>
          <Field span={4}>
            <TextField
              label="Fee Amount"
              type="number"
              value={values.fee_paid_amount}
              onChange={setValue("fee_paid_amount")}
              fullWidth
              disabled={values.fee_paid !== "yes"}
              sx={FIELD_SX}
            />
          </Field>
          <Field span={4}>
            <TextField label="Guest Count" type="number" value={values.guests_count} onChange={setValue("guests_count")} fullWidth sx={FIELD_SX} />
          </Field>
          <Field span={4}>
            <TextField label="Checked In At" type="datetime-local" value={values.attended_at} onChange={setValue("attended_at")} fullWidth InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
          </Field>
          <Field span={12}>
            <TextField label="Notes" value={values.notes} onChange={setValue("notes")} fullWidth multiline minRows={3} sx={FIELD_SX} />
          </Field>
          <Field span={12}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {[
                ["is_sponsor_attendee", "Sponsor attendee"],
                ["is_speaker", "Speaker"],
                ["is_exhibitor", "Exhibitor"],
              ].map(([field, label]) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={values[field]}
                      onChange={setValue(field)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<RadioButtonCheckedIcon />}
                      sx={{ color: BRAND_BLUE, "&.Mui-checked": { color: BRAND_GREEN } }}
                    />
                  }
                  label={label}
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { color: BRAND_BLUE, fontWeight: 700 } }}
                />
              ))}
            </Stack>
          </Field>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
        <Button onClick={submit} disabled={saving || (!values.member_id && !values.contact_id)} sx={PRIMARY_BUTTON_SX}>{saving ? "Saving..." : "Save Attendance"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function EventsHeader({ title, searchTerm, onSearchChange, onActionsClick, detail = false, backTo }) {
  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 5, borderBottom: "1px solid #dce4ef", background: "#ffffff" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: { xs: 2, md: 3 }, py: 1.75, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <BackButton to={backTo} />
          <Box sx={{ width: 32, height: 32, borderRadius: 2, background: BRAND_GREEN, color: "#fff", display: "grid", placeItems: "center" }}>
            <CalendarMonthIcon sx={{ fontSize: 19 }} />
          </Box>
          <Typography sx={{ fontSize: 21, fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.05 }}>{title}</Typography>
        </Box>
        {!detail ? <Box sx={{ flex: 1, minWidth: 240 }}><SearchBar value={searchTerm} onChange={onSearchChange} placeholder="Search event name, type, host, audience, program, or location..." maxWidth={1700} /></Box> : <Box sx={{ flex: 1 }} />}
        <Button endIcon={<KeyboardArrowDownIcon />} onClick={onActionsClick} sx={ACTIONS_HEADER_BTN_SX}>Actions</Button>
      </Box>
    </Box>
  );
}

function FilterBar({ filters, onChange }) {
  return (
    <Card sx={{ ...CARD_SX, mb: 2 }}>
      <CardContent sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: "12px" }}>
        <FormControl size="small" sx={FILTER_STYLES.formControl}>
          <Select value={filters.status} onChange={(e) => onChange("status", e.target.value)} {...FILTER_STYLES.select}>
            {["", "planned", "open", "completed", "cancelled"].map((v) => <MenuItem key={v} value={v}>{v || "All Statuses"}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Event Type" value={filters.event_type} onChange={(e) => onChange("event_type", e.target.value)} size="small" sx={FILTER_STYLES.formControl} />
        <TextField label="Target Audience" value={filters.target_audience} onChange={(e) => onChange("target_audience", e.target.value)} size="small" sx={FILTER_STYLES.formControl} />
        <TextField label="Program Area" value={filters.program_area} onChange={(e) => onChange("program_area", e.target.value)} size="small" sx={FILTER_STYLES.formControl} />
      </CardContent>
    </Card>
  );
}

function EventMetrics({ events, attendance, event }) {
  const actualAttendance = attendance.filter((row) => row.status === "attended" || row.check_in_status === "checked_in").length;
  const noShows = attendance.filter((row) => row.status === "no_show" || row.check_in_status === "no_show").length;
  const speakers = attendance.filter((row) => row.is_speaker).length;
  const exhibitors = attendance.filter((row) => row.is_exhibitor).length;
  const sponsorAttendees = attendance.filter((row) => row.is_sponsor_attendee).length;

  const cards = event
    ? [
        ["Registered", attendance.length, "Total attendee records"],
        ["Actual Attendance", actualAttendance, `Capacity ${event.capacity || "-"}`],
        ["No-shows", noShows, "Registration status or check-in no-show"],
        ["Speakers / Exhibitors", `${speakers} / ${exhibitors}`, `${sponsorAttendees} sponsor attendees`],
      ]
    : [
        ["Events", events.length, "Visible on this page"],
        ["Upcoming/Open", events.filter((item) => ["planned", "open"].includes(item.status)).length, "Planned or open events"],
        ["Capacity", events.reduce((sum, item) => sum + Number(item.capacity || 0), 0), "Visible event capacity"],
        ["Fees", events.filter((item) => Number(item.registration_fee || 0) > 0).length, "Paid registration events"],
      ];

  return <Box sx={{ ...AUTO_GRID_SX, mb: 2 }}>{cards.map(([label, value, helper]) => <MetricCard key={label} label={label} value={value} helper={helper} />)}</Box>;
}

function EventsList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [filters, setFilters] = useState({ status: "", event_type: "", target_audience: "", program_area: "" });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [orderBy, setOrderBy] = useState("starts_at");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { events, totalCount, loading, error, refetch } = useMembershipEventsData({ page, pageSize, searchTerm: debouncedSearch, filters, orderBy, orderDirection });

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const handleSort = (field) => {
    setOrderBy(field);
    setOrderDirection(orderBy === field && orderDirection === "asc" ? "desc" : "asc");
    setPage(1);
  };
  const saveEvent = async (payload) => {
    setSaving(true);
    try {
      if (activeEvent?.id) await api.patch(`/membership/events/${activeEvent.id}`, payload);
      else await api.post("/membership/events", payload);
      setDialogOpen(false);
      setActiveEvent(null);
      showSnackbar("Event saved.");
      refetch();
    } catch {
      showSnackbar("Failed to save event.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <EventsHeader title="Events" searchTerm={searchTerm} onSearchChange={(value) => { setSearchTerm(value); setPage(1); }} onActionsClick={(e) => setActionsAnchor(e.currentTarget)} backTo="/membership" />
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <EventMetrics events={events} attendance={[]} />
        <FilterBar filters={filters} onChange={(key, value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPage(1); }} />
        <Card sx={CARD_SX}>
          {loading ? <BrandSpinner compact label="Loading events..." py={8} /> : error ? <CardContent><Alert severity="error">{error}</Alert></CardContent> : events.length === 0 ? (
            <CardContent sx={{ p: 5, textAlign: "center" }}><Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 20 }}>No events found</Typography><Button startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ ...PRIMARY_BUTTON_SX, mt: 3 }}>Create Event</Button></CardContent>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1260 }}>
                <TableHead><TableRow>{[
                  ["name", "Event name"], ["event_type", "Type"], ["starts_at", "Event date"], ["venue_name", "Location"], ["host_organization", "Host organization"], ["registration_fee", "Fee"], ["capacity", "Capacity"], ["target_audience", "Target audience"], ["program_area", "Program area"], ["status", "Status"], ["actions", "Actions"],
                ].map(([key, label]) => <TableCell key={key} sx={HEAD_CELL_SX}>{["name", "starts_at", "registration_fee", "capacity", "status"].includes(key) ? <TableSortLabel active={orderBy === key} direction={orderBy === key ? orderDirection : "asc"} onClick={() => handleSort(key)} sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}>{label}</TableSortLabel> : label}</TableCell>)}</TableRow></TableHead>
                <TableBody>{events.map((event, index) => <TableRow key={event.id} sx={{ bgcolor: index % 2 === 0 ? "#fafafa" : "#fff" }}>
                  <TableCell><Button variant="text" onClick={() => navigate(`/membership/events/${event.id}`)} sx={{ color: BRAND_BLUE, fontWeight: 800, textTransform: "none" }}>{event.name}</Button></TableCell>
                  <TableCell>{display(event.event_type)}</TableCell><TableCell>{formatDateTime(event.starts_at)}</TableCell><TableCell>{display(event.venue_name || event.city)}</TableCell><TableCell>{display(event.host_organization)}</TableCell><TableCell>{formatMoney(event.registration_fee)}</TableCell><TableCell>{display(event.capacity)}</TableCell><TableCell>{display(event.target_audience)}</TableCell><TableCell>{display(event.program_area)}</TableCell><TableCell>{display(event.status)}</TableCell>
                  <TableCell><Tooltip title="Edit event" {...MEMBERSHIP_TOOLTIP_PROPS}><IconButton size="small" onClick={() => { setActiveEvent(event); setDialogOpen(true); }}><EditIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></IconButton></Tooltip></TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
        {!error && totalCount > pageSize ? <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}><BrandPagination page={page} count={Math.ceil(totalCount / pageSize) || 1} onChange={(_e, value) => setPage(value)} /></Box> : null}
      </Box>
      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)} PaperProps={{ sx: ACTION_MENU_PAPER_SX }}>
        <MenuItem onClick={() => { setActionsAnchor(null); setActiveEvent(null); setDialogOpen(true); }}><AddIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }} />Create Event</MenuItem>
        <MenuItem onClick={() => { setActionsAnchor(null); refetch(); }}><RefreshIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.refresh }} />Refresh</MenuItem>
      </Menu>
      <MembershipEventDialog open={dialogOpen} event={activeEvent} saving={saving} onClose={() => setDialogOpen(false)} onSubmit={saveEvent} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
}

function EventDetail() {
  const { id } = useParams();
  const eventId = Number(id);
  const [event, setEvent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [activeAttendance, setActiveAttendance] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eventRes, attendanceRows, sponsorRows, memberRows, contactRows] = await Promise.all([
        api.get(`/membership/events/${eventId}`),
        listEndpoint("/membership/event-attendance", { event_id: eventId, sort_by: "created_at", sort_dir: "desc" }),
        listEndpoint("/membership/sponsorships", { event_id: eventId }),
        listEndpoint("/membership/members", { sort_by: "name", sort_dir: "asc" }),
        listEndpoint("/membership/contacts", { sort_by: "full_name", sort_dir: "asc" }),
      ]);
      setEvent(eventRes.data); setAttendance(attendanceRows); setSponsorships(sponsorRows); setMembers(memberRows); setContacts(contactRows);
    } finally { setLoading(false); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const saveEvent = async (payload) => {
    setSaving(true);
    try { await api.patch(`/membership/events/${eventId}`, payload); setEventDialogOpen(false); await load(); } finally { setSaving(false); }
  };
  const saveAttendance = async (payload) => {
    setSaving(true);
    try {
      if (activeAttendance?.id) await api.patch(`/membership/event-attendance/${activeAttendance.id}`, payload);
      else await api.post("/membership/event-attendance", payload);
      setAttendanceDialogOpen(false); setActiveAttendance(null); await load();
    } finally { setSaving(false); }
  };

  if (loading) return <BrandSpinner label="Loading event..." />;
  if (!event) return <Box sx={{ p: 3 }}><Alert severity="error">Failed to load event.</Alert></Box>;

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <EventsHeader title={event.name} detail onActionsClick={(e) => setActionsAnchor(e.currentTarget)} backTo="/membership/events" />
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <EventMetrics event={event} events={[]} attendance={attendance} />
        <Card sx={{ ...CARD_SX, mb: 2 }}><CardContent sx={{ p: 3 }}><Box sx={AUTO_GRID_SX}>
          {[["Event type", event.event_type], ["Event date", formatDateTime(event.starts_at)], ["Location", event.venue_name || event.city], ["Host organization", event.host_organization], ["Registration fee", formatMoney(event.registration_fee)], ["Capacity", event.capacity], ["Target audience", event.target_audience], ["Program area", event.program_area], ["Status", event.status]].map(([label, value]) => <Box key={label}><Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>{label}</Typography><Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>{display(value)}</Typography></Box>)}
        </Box></CardContent></Card>
        <Stack spacing={2}>
          <Card sx={CARD_SX}><CardContent sx={{ p: 2.5 }}><Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}><Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 18 }}>Attendance</Typography><Button startIcon={<HowToRegIcon />} onClick={() => { setActiveAttendance(null); setAttendanceDialogOpen(true); }} sx={PRIMARY_BUTTON_SX}>Add Attendance</Button></Box>
            <TableContainer sx={{ overflowX: "auto" }}><Table size="small" sx={{ minWidth: 1160 }}><TableHead><TableRow>{["Member", "Contact", "Registration", "Check-in", "Fee Paid", "Fee Amount", "Guests", "Sponsor", "Speaker", "Exhibitor", "Actions"].map((h) => <TableCell key={h} sx={HEAD_CELL_SX}>{h}</TableCell>)}</TableRow></TableHead><TableBody>{attendance.map((row) => <TableRow key={row.id}><TableCell>{display(members.find((m) => m.id === row.member_id)?.name || row.member_id)}</TableCell><TableCell>{display(contacts.find((c) => c.id === row.contact_id)?.full_name || row.contact_id)}</TableCell><TableCell>{display(row.status)}</TableCell><TableCell>{display(row.check_in_status)}</TableCell><TableCell>{row.fee_paid == null ? "-" : Number(row.fee_paid) > 0 ? "Yes" : "No"}</TableCell><TableCell>{Number(row.fee_paid) > 0 ? formatMoney(row.fee_paid) : "-"}</TableCell><TableCell>{display(row.guests_count)}</TableCell><TableCell>{row.is_sponsor_attendee ? "Yes" : "No"}</TableCell><TableCell>{row.is_speaker ? "Yes" : "No"}</TableCell><TableCell>{row.is_exhibitor ? "Yes" : "No"}</TableCell><TableCell><IconButton size="small" onClick={() => { setActiveAttendance(row); setAttendanceDialogOpen(true); }}><EditIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></IconButton></TableCell></TableRow>)}</TableBody></Table></TableContainer>
          </CardContent></Card>
          <Card sx={CARD_SX}><CardContent sx={{ p: 2.5 }}><Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 18, mb: 2 }}>Sponsors Attached to Event</Typography><TableContainer sx={{ overflowX: "auto" }}><Table size="small"><TableHead><TableRow>{["Sponsor", "Level", "Status", "Amount", "Notes"].map((h) => <TableCell key={h} sx={HEAD_CELL_SX}>{h}</TableCell>)}</TableRow></TableHead><TableBody>{sponsorships.map((row) => <TableRow key={row.id}><TableCell>{display(row.name)}</TableCell><TableCell>{display(row.sponsorship_level)}</TableCell><TableCell>{display(row.status)}</TableCell><TableCell>{formatMoney(row.amount)}</TableCell><TableCell>{display(row.notes)}</TableCell></TableRow>)}</TableBody></Table></TableContainer></CardContent></Card>
        </Stack>
      </Box>
      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)} PaperProps={{ sx: ACTION_MENU_PAPER_SX }}>
        <MenuItem onClick={() => { setActionsAnchor(null); setEventDialogOpen(true); }}><EditIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }} />Edit Event</MenuItem>
        <MenuItem onClick={() => { setActionsAnchor(null); load(); }}><RefreshIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.refresh }} />Refresh</MenuItem>
      </Menu>
      <MembershipEventDialog open={eventDialogOpen} event={event} saving={saving} onClose={() => setEventDialogOpen(false)} onSubmit={saveEvent} />
      <AttendanceDialog open={attendanceDialogOpen} attendance={activeAttendance} eventId={eventId} members={members} contacts={contacts} saving={saving} onClose={() => setAttendanceDialogOpen(false)} onSubmit={saveAttendance} />
    </Box>
  );
}

export default function MembershipEvents() {
  const { id } = useParams();
  return id ? <EventDetail /> : <EventsList />;
}
